import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiPlus, FiRefreshCw, FiX, FiAlertTriangle, FiCheck, FiCalendar, FiSearch } from 'react-icons/fi';
import { foundationAPI, settlingAPI, teamTasksAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './VisaCases.css';
import './Meetings.css';

// Team tasks (brainstorms/2026-10-17-foundation-enforcement.md, Q5). One Accountable person (A) per
// task, one or more Responsible (R). Only A moves a due date; every change is logged so KPI V2
// (done on time) can be measured. R marks done and A is told; A can reopen within 7 days.

const STATUS_LABEL = { OPEN: 'Open', IN_PROGRESS: 'In progress', AT_RISK: 'At risk', DONE: 'Done', CANCELLED: 'Cancelled' };
const STATUS_TONE = { OPEN: '', IN_PROGRESS: 'vc-status-open', AT_RISK: 'vc-badge-warn', DONE: 'vc-status-approved', CANCELLED: 'vc-status-refused' };
const EVENT_LABEL = { CREATED: 'Created', EDITED: 'Edited', STATUS: 'Status', DUE_CHANGED: 'Due date moved', REOPENED: 'Reopened', COMMENT: 'Comment' };
const KINDS = [['STUDENT', 'Student'], ['JOB', 'Job seeker'], ['TOURIST', 'Tourist'], ['MEDICAL', 'Medical client']];

const errText = (err, fallback) => {
  const d = err?.response?.data || {};
  return d.error || d.detail || Object.values(d).flat()[0] || fallback;
};
const fmtDate = (v) => (v ? new Date(`${String(v).slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const TeamTasks = () => {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState('mine');
  const [status, setStatus] = useState('');
  const [person, setPerson] = useState('');
  const [tasks, setTasks] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const openId = params.get('task');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const q = { scope: tab === 'closed' ? 'closed' : 'active' };
    if (tab === 'mine') q.mine = 1;
    if (status) q.status = status;
    if (person && tab !== 'mine') q.person = person;
    try {
      const res = await teamTasksAPI.list(q);
      setTasks(res.data?.results ?? res.data ?? []);
    } catch (err) {
      setError(errText(err, 'Could not load tasks.'));
    } finally {
      setLoading(false);
    }
  }, [tab, status, person]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { teamTasksAPI.people().then((r) => setPeople(r.data)).catch(() => {}); }, []);

  const open = (id) => setParams(id ? { task: String(id) } : {});
  const overdue = tasks.filter((t) => t.overdue).length;
  const atRisk = tasks.filter((t) => t.status === 'AT_RISK').length;

  return (
    <div className="mt-page">
      <div className="vc-header">
        <div>
          <h1 className="mt-title">Team tasks</h1>
          <p className="vc-muted">Every task has one Accountable person (A) and one or more Responsible (R). Only A moves a due date.</p>
        </div>
        <div className="vc-header-actions">
          <button type="button" className="vc-btn" onClick={load}><FiRefreshCw /> Refresh</button>
          <button type="button" className="vc-btn vc-btn-primary" onClick={() => setCreating(true)}><FiPlus /> New task</button>
        </div>
      </div>

      {tab !== 'closed' && (
        <div className="vc-stats">
          <div className="vc-stat"><div className="vc-stat-value">{tasks.length}</div><div className="vc-stat-label">Open tasks</div></div>
          <div className="vc-stat"><div className={`vc-stat-value ${overdue ? 'vc-bad' : ''}`}>{overdue}</div><div className="vc-stat-label">Overdue</div></div>
          <div className="vc-stat"><div className="vc-stat-value">{atRisk}</div><div className="vc-stat-label">At risk</div></div>
        </div>
      )}

      <div className="vc-filters">
        <div className="vc-tabs">
          <button type="button" className={tab === 'mine' ? 'active' : ''} onClick={() => setTab('mine')}>My tasks</button>
          <button type="button" className={tab === 'team' ? 'active' : ''} onClick={() => setTab('team')}>Whole team</button>
          <button type="button" className={tab === 'closed' ? 'active' : ''} onClick={() => setTab('closed')}>Done and cancelled</button>
        </div>
        {tab !== 'closed' && (
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Any status</option>
            {['OPEN', 'IN_PROGRESS', 'AT_RISK'].map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        )}
        {tab !== 'mine' && (
          <select value={person} onChange={(e) => setPerson(e.target.value)}>
            <option value="">Everyone</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>

      {error && <div className="vc-error">{error}</div>}
      {loading ? <div className="vc-empty">Loading…</div> : tasks.length === 0 ? (
        <div className="vc-empty">{tab === 'mine' ? 'Nothing assigned to you.' : 'No tasks here.'}</div>
      ) : (
        <div className="vc-table">
          {tasks.map((t) => (
            <button type="button" key={t.id} className="vc-rowcard" onClick={() => open(t.id)}>
              <div>
                <div className="mt-row-title">{t.title}</div>
                <div className="vc-muted">
                  A: {t.accountable_detail.name} · R: {t.responsible_detail.map((r) => r.name).join(', ')}
                  {t.meeting_title ? ` · from ${t.meeting_title}` : ''}{t.client_label ? ` · ${t.client_label}` : ''}
                </div>
              </div>
              <div className="vc-rowcard-side">
                {t.my_role.length > 0 && <span className="mt-roles">{t.my_role.map((r) => <span key={r} className="vc-badge vc-badge-info">{r}</span>)}</span>}
                <span className={t.overdue ? 'mt-overdue' : 'vc-muted'}><FiCalendar /> {fmtDate(t.due_date)}</span>
                {t.status === 'DONE' && t.completed_on_time !== null && (
                  <span className={`vc-badge ${t.completed_on_time ? 'vc-badge-ok' : 'vc-badge-bad'}`}>{t.completed_on_time ? 'On time' : 'Late'}</span>
                )}
                <span className={`vc-status ${STATUS_TONE[t.status]}`}>{STATUS_LABEL[t.status]}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {creating && <TaskForm people={people} onClose={() => setCreating(false)} onSaved={(t) => { setCreating(false); load(); open(t.id); }} />}
      {openId && <TaskDetail id={openId} people={people} onClose={() => { open(null); load(); }} />}
    </div>
  );
};

const ClientPicker = ({ value, onChange }) => {
  const [kind, setKind] = useState(value.client_kind || 'STUDENT');
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => {
      settlingAPI.searchClients({ kind, q }).then((r) => setResults(r.data)).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q, kind]);

  if (value.client_id) {
    return (
      <div className="vc-picked">
        <span>{value.client_label}</span>
        <button type="button" className="vc-icon-btn" onClick={() => onChange({ client_kind: '', client_id: null, client_label: '' })}><FiX /></button>
      </div>
    );
  }
  return (
    <div className="vc-form">
      <div className="vc-row">
        <label className="vc-label">Client type
          <select value={kind} onChange={(e) => setKind(e.target.value)}>{KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        </label>
        <div className="vc-label">Search
          <div className="vc-search"><FiSearch /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or reference" /></div>
        </div>
      </div>
      {results.length > 0 && (
        <ul className="vc-results">
          {results.map((c) => (
            <li key={c.id}><button type="button" onClick={() => onChange({ client_kind: kind, client_id: c.id, client_label: `${c.name} (${c.ref})` })}>{c.name} · {c.ref}</button></li>
          ))}
        </ul>
      )}
    </div>
  );
};

const TaskForm = ({ task, people, onClose, onSaved }) => {
  const { user } = useAuth();
  const [f, setF] = useState({
    title: task?.title || '',
    description: task?.description || '',
    accountable: task?.accountable || user?.id || '',
    responsible: task ? task.responsible_detail.map((r) => r.id) : [],
    due_date: task?.due_date || '',
    raci_ref: task?.raci_ref || '',
    client_kind: task?.client_kind || '',
    client_id: task?.client_id || null,
    client_label: task?.client_label || '',
  });
  const [raciHint, setRaciHint] = useState(null);
  // Who the RACI chart in force makes Accountable for the row typed (Foundation Q14).
  const lookUpRaci = () => {
    const code = f.raci_ref.trim();
    if (!code) { setRaciHint(null); return; }
    foundationAPI.raciRow(code).then((r) => setRaciHint(r.data)).catch(() => setRaciHint({ error: `No RACI row ${code.toUpperCase()}.` }));
  };
  const [linkClient, setLinkClient] = useState(Boolean(task?.client_id));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggleR = (id) => set('responsible', f.responsible.includes(id) ? f.responsible.filter((x) => x !== id) : [...f.responsible, id]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const data = { ...f, accountable: Number(f.accountable) };
    try {
      let res;
      if (task) {
        delete data.due_date;
        res = await teamTasksAPI.update(task.id, data);
      } else {
        res = await teamTasksAPI.create(data);
      }
      onSaved(res.data);
    } catch (err) {
      setError(errText(err, 'Could not save the task.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="vc-overlay" onClick={onClose}>
      <div className="vc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vc-modal-head"><h2>{task ? 'Edit task' : 'New task'}</h2><button type="button" className="vc-icon-btn" onClick={onClose}><FiX /></button></div>
        <form className="vc-form" onSubmit={save}>
          <label className="vc-label">What needs doing
            <input value={f.title} onChange={(e) => set('title', e.target.value)} required />
          </label>
          <label className="vc-label">Details
            <textarea className="vc-note" rows={3} value={f.description} onChange={(e) => set('description', e.target.value)} />
          </label>
          <div className="vc-row">
            <label className="vc-label">Accountable (exactly one)
              <select value={f.accountable} onChange={(e) => set('accountable', e.target.value)} required>
                {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            {!task && (
              <label className="vc-label">Due date
                <input type="date" value={f.due_date} onChange={(e) => set('due_date', e.target.value)} required />
              </label>
            )}
            <label className="vc-label">RACI row (optional)
              <input value={f.raci_ref} onChange={(e) => set('raci_ref', e.target.value)} onBlur={lookUpRaci} placeholder="e.g. D3" />
              {raciHint && (raciHint.error ? <span className="vc-muted" style={{ color: '#b91c1c' }}>{raciHint.error}</span> : (
                <span className="vc-muted">
                  RACI {raciHint.code}: {raciHint.accountable.role_name} is Accountable
                  {raciHint.accountable.people.length === 1 && String(raciHint.accountable.people[0].id) !== String(f.accountable) && (
                    <> · <button type="button" className="vc-link" onClick={() => set('accountable', raciHint.accountable.people[0].id)}>make {raciHint.accountable.people[0].name} Accountable</button></>
                  )}
                </span>
              ))}
            </label>
          </div>
          <div className="vc-label">Responsible (leave empty for the Accountable person)
            <div className="mt-invitees">
              {people.map((p) => (
                <label key={p.id} className="vc-check"><input type="checkbox" checked={f.responsible.includes(p.id)} onChange={() => toggleR(p.id)} /> {p.name}</label>
              ))}
            </div>
          </div>
          <label className="vc-check"><input type="checkbox" checked={linkClient} onChange={(e) => { setLinkClient(e.target.checked); if (!e.target.checked) setF((x) => ({ ...x, client_kind: '', client_id: null, client_label: '' })); }} /> Link to a client</label>
          {linkClient && <ClientPicker value={f} onChange={(v) => setF((x) => ({ ...x, ...v }))} />}
          {error && <div className="vc-error">{error}</div>}
          <div className="vc-modal-actions">
            <button type="button" className="vc-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="vc-btn vc-btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TaskDetail = ({ id, people, onClose }) => {
  const [t, setT] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null); // 'risk' | 'due' | 'cancel' | 'reopen'
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [comment, setComment] = useState('');
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    teamTasksAPI.get(id).then((r) => setT(r.data)).catch((err) => setError(errText(err, 'Could not load the task.')));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      const res = await fn();
      setT(res.data);
      setForm(null); setA(''); setB('');
      return true;
    } catch (err) {
      setError(errText(err, 'That did not work.'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (!t) {
    return <div className="vc-overlay" onClick={onClose}><div className="vc-modal">{error ? <div className="vc-error">{error}</div> : 'Loading…'}</div></div>;
  }
  const isA = t.my_role.includes('A');
  const isR = t.my_role.includes('R');
  const active = ['OPEN', 'IN_PROGRESS', 'AT_RISK'].includes(t.status);
  const moved = t.due_date !== t.original_due_date;

  return (
    <div className="vc-overlay" onClick={onClose}>
      <div className="vc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vc-modal-head">
          <h2>{t.title}</h2>
          <button type="button" className="vc-icon-btn" onClick={onClose}><FiX /></button>
        </div>
        <div className="vc-badges">
          <span className={`vc-status ${STATUS_TONE[t.status]}`}>{STATUS_LABEL[t.status]}</span>
          {t.overdue && <span className="vc-badge vc-badge-bad">Overdue</span>}
          {t.status === 'DONE' && t.completed_on_time !== null && (
            <span className={`vc-badge ${t.completed_on_time ? 'vc-badge-ok' : 'vc-badge-bad'}`}>{t.completed_on_time ? 'Done on time' : 'Done late'}</span>
          )}
        </div>
        {t.description && <div className="mt-pre" style={{ marginTop: 10 }}>{t.description}</div>}
        <div className="vc-flags">
          <div><span className="vc-muted">Accountable</span><br />{t.accountable_detail.name}</div>
          <div><span className="vc-muted">Responsible</span><br />{t.responsible_detail.map((r) => r.name).join(', ')}</div>
          <div><span className="vc-muted">Due</span><br />{fmtDate(t.due_date)}{moved && <span className="vc-muted"> (first set for {fmtDate(t.original_due_date)}; counts as {fmtDate(t.effective_deadline)})</span>}</div>
          {t.meeting_title && <div><span className="vc-muted">From meeting</span><br />{t.meeting_title}</div>}
          {t.client_label && <div><span className="vc-muted">Client</span><br />{t.client_label}</div>}
          {t.raci_ref && <div><span className="vc-muted">RACI</span><br />{t.raci_ref}</div>}
        </div>
        {t.status === 'AT_RISK' && (
          <div className="vc-route vc-route-warn"><FiAlertTriangle /> {t.at_risk_reason}<br /><strong>Needs:</strong> {t.at_risk_needs}</div>
        )}
        {t.status === 'CANCELLED' && t.cancel_reason && <div className="vc-route vc-route-bad">Cancelled: {t.cancel_reason}</div>}
        {error && <div className="vc-error">{error}</div>}

        <div className="vc-share-actions">
          {active && (isR || isA) && t.status === 'OPEN' && <button type="button" className="vc-btn vc-btn-sm" disabled={busy} onClick={() => run(() => teamTasksAPI.setStatus(t.id, { status: 'IN_PROGRESS' }))}>Start</button>}
          {active && (isR || isA) && t.status === 'AT_RISK' && <button type="button" className="vc-btn vc-btn-sm" disabled={busy} onClick={() => run(() => teamTasksAPI.setStatus(t.id, { status: 'IN_PROGRESS' }))}>Back on track</button>}
          {active && (isR || isA) && t.status !== 'AT_RISK' && <button type="button" className="vc-btn vc-btn-sm" onClick={() => setForm('risk')}><FiAlertTriangle /> At risk</button>}
          {active && (isR || isA) && <button type="button" className="vc-btn vc-btn-sm vc-btn-primary" disabled={busy} onClick={() => run(() => teamTasksAPI.setStatus(t.id, { status: 'DONE' }))}><FiCheck /> Done</button>}
          {active && isA && <button type="button" className="vc-btn vc-btn-sm" onClick={() => { setForm('due'); setA(t.due_date); }}><FiCalendar /> Move due date</button>}
          {active && isA && <button type="button" className="vc-btn vc-btn-sm" onClick={() => setForm('cancel')}>Cancel task</button>}
          {t.can_reopen && <button type="button" className="vc-btn vc-btn-sm" onClick={() => setForm('reopen')}>Reopen</button>}
          {t.can_edit && active && <button type="button" className="vc-btn vc-btn-sm" onClick={() => setEditing(true)}>Edit</button>}
        </div>

        {form === 'risk' && (
          <div className="vc-item-form">
            <label className="vc-label">Why is it at risk?<input value={a} onChange={(e) => setA(e.target.value)} /></label>
            <label className="vc-label">What do you need to get it back on track?<input value={b} onChange={(e) => setB(e.target.value)} /></label>
            <div className="vc-item-form-actions">
              <button type="button" className="vc-btn" onClick={() => setForm(null)}>Back</button>
              <button type="button" className="vc-btn vc-btn-primary" disabled={busy || !a.trim() || !b.trim()} onClick={() => run(() => teamTasksAPI.setStatus(t.id, { status: 'AT_RISK', reason: a, needs: b }))}>Flag at risk</button>
            </div>
          </div>
        )}
        {form === 'due' && (
          <div className="vc-item-form">
            <p className="vc-muted">A new date agreed before the current one passes still counts as on time. Moving it after the date has passed is logged as late.</p>
            <label className="vc-label">New due date<input type="date" value={a} onChange={(e) => setA(e.target.value)} /></label>
            <label className="vc-label">Why (optional)<input value={b} onChange={(e) => setB(e.target.value)} /></label>
            <div className="vc-item-form-actions">
              <button type="button" className="vc-btn" onClick={() => setForm(null)}>Back</button>
              <button type="button" className="vc-btn vc-btn-primary" disabled={busy || !a} onClick={() => run(() => teamTasksAPI.moveDue(t.id, { due_date: a, note: b }))}>Move date</button>
            </div>
          </div>
        )}
        {(form === 'cancel' || form === 'reopen') && (
          <div className="vc-item-form">
            <label className="vc-label">{form === 'cancel' ? 'Why is it cancelled?' : 'What still needs doing?'}<input value={a} onChange={(e) => setA(e.target.value)} /></label>
            <div className="vc-item-form-actions">
              <button type="button" className="vc-btn" onClick={() => setForm(null)}>Back</button>
              <button type="button" className="vc-btn vc-btn-primary" disabled={busy || (form === 'cancel' && !a.trim())}
                onClick={() => run(() => (form === 'cancel' ? teamTasksAPI.cancel(t.id, a) : teamTasksAPI.reopen(t.id, a)))}>
                {form === 'cancel' ? 'Cancel task' : 'Reopen'}
              </button>
            </div>
          </div>
        )}

        <h4 className="mt-sub">History</h4>
        <ul className="mt-events">
          {t.events.map((ev) => (
            <li key={ev.id}>
              <span className="vc-muted">{fmtDateTime(ev.created_at)} · {ev.actor_name}</span><br />
              <strong>{EVENT_LABEL[ev.kind]}</strong>
              {ev.kind === 'STATUS' && `: ${STATUS_LABEL[ev.from_value] || ev.from_value} → ${STATUS_LABEL[ev.to_value] || ev.to_value}`}
              {ev.kind === 'DUE_CHANGED' && `: ${fmtDate(ev.from_value)} → ${fmtDate(ev.to_value)}`}
              {ev.note && <div className="mt-pre">{ev.note}</div>}
            </li>
          ))}
        </ul>
        <div className="vc-item-form" style={{ marginLeft: 0, marginTop: 10 }}>
          <textarea className="vc-note" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment" />
          <div className="vc-item-form-actions">
            <button type="button" className="vc-btn vc-btn-sm" disabled={busy || !comment.trim()}
              onClick={async () => { if (await run(() => teamTasksAPI.comment(t.id, comment))) setComment(''); }}>Comment</button>
          </div>
        </div>
        {editing && <TaskForm task={t} people={people} onClose={() => setEditing(false)} onSaved={(saved) => { setEditing(false); setT(saved); }} />}
      </div>
    </div>
  );
};

export default TeamTasks;
